import { z } from 'zod';
import { localizedTextSchema } from '../access/index';
import { audienceSpecSchema } from '../audience/index';

/**
 * Contracten van de module forms (Stap 37): een verlofaanvraag, een materiaalaanvraag, een
 * melding van een defect — een beheerder bouwt het formulier, de medewerker dient het in vanaf
 * zijn telefoon, en een behandelaar keurt goed of af.
 *
 * Twee dingen bepalen de hele vorm hieronder.
 *
 * **Een inzending hoort bij een versie, niet bij een formulier.** Wie in maart een verlofaanvraag
 * indiende met drie velden, moet die in juni nog kunnen lezen zoals ze toen was — ook als het
 * formulier intussen vijf velden heeft. Daarom verwijst een inzending naar `form_version_id` en
 * niet naar `form_id`, en is een gepubliceerde versie onveranderlijk.
 *
 * **De velden zijn een eigen catalogus en geen JSON Schema.** Waarom dat zo is, staat in
 * ADR-0061; kort: JSON Schema kan "een datumbereik waarvan het einde niet voor het begin ligt"
 * niet uitdrukken zonder uitbreidingen, en kan "dit veld wordt door de server gevuld en de
 * medewerker mag het niet overschrijven" al helemaal niet.
 */

/**
 * De veldtypes.
 *
 * `signature` staat er **niet** bij, en dat wijkt af van de bouwprompt: een handtekening vraagt
 * een tekenvlak en een eigen mediadoel, en een "handtekening" die een getypte naam is, is erger
 * dan geen handtekening — ze ziet eruit als bewijs. Genoteerd in `docs/product/demo-schuld.md`.
 */
export const formFieldTypeSchema = z.enum([
  'text',
  'longtext',
  'number',
  'date',
  'daterange',
  'choice',
  'multichoice',
  'photo',
]);
export type FormFieldType = z.infer<typeof formFieldTypeSchema>;

/**
 * Waar een veld zijn beginwaarde vandaan haalt.
 *
 * `employee_number` en `full_name` komen uit `directory`; `field` leest een gegevensveld van het
 * bedrijf (`prefillKey`). Een voorgevuld veld kan **vast** staan (`locked`), en dan negeert de
 * server wat de client stuurde — zie `applyPrefill`. Dat is acceptatiecriterium 3, en het is een
 * serverregel: een invoerveld dat alleen in de browser `disabled` is, is geen regel maar een
 * suggestie.
 */
export const formPrefillSchema = z.enum(['none', 'employee_number', 'full_name', 'field']);
export type FormPrefill = z.infer<typeof formPrefillSchema>;

export const formFieldOptionSchema = z.object({
  value: z.string().min(1).max(80),
  label: localizedTextSchema,
});
export type FormFieldOption = z.infer<typeof formFieldOptionSchema>;

export const formFieldSchema = z.object({
  /**
   * De sleutel in `submission.data`, en dus de kolomnaam in de CSV-export.
   *
   * Door de beheerder te kiezen en niet een gegenereerde id: een export die kolommen
   * `f1, f2, f3` heeft, is onbruikbaar voor de loonadministratie die hem moet verwerken.
   */
  key: z
    .string()
    .regex(/^[a-z][a-z0-9_]{0,39}$/, 'Gebruik kleine letters, cijfers en liggende streepjes.'),
  type: formFieldTypeSchema,
  label: localizedTextSchema,
  help: localizedTextSchema.optional(),
  required: z.boolean(),
  /** Alleen bij `choice` en `multichoice`; elders leeg. */
  options: z.array(formFieldOptionSchema).max(50).default([]),
  /** Alleen bij `number`: onder- en bovengrens, beide optioneel. */
  min: z.number().nullable().default(null),
  max: z.number().nullable().default(null),
  prefill: formPrefillSchema.default('none'),
  /** De sleutel van het gegevensveld, alleen bij `prefill: 'field'`. */
  prefillKey: z.string().max(60).nullable().default(null),
  /** Voorgevuld én niet te wijzigen. Zonder prefill zinloos, en dan weigert de server het. */
  locked: z.boolean().default(false),
});
export type FormField = z.infer<typeof formFieldSchema>;

/**
 * Wie de inzendingen behandelt.
 *
 * `none` is een formulier dat alleen verzameld wordt (een melding, een inschrijving); `group`
 * wijst een vaste groep aan, en ieder lid daarvan mag beslissen.
 *
 * `manager` wijst de eigen leidinggevende aan (Stap 17, TEAM-1, demo-schuld 37): de aanvraag gaat
 * naar wie de inzender opvolgt - zijn leidinggevende en de beheerders van zijn groepen. Zo'n
 * formulier heeft geen vaste inbox maar één per aanvraag, en dat is wat een verlofaanvraag nodig
 * heeft.
 *
 * Deze waarde ontbrak tot Stap 17 met opzet: zonder `directory.reporting_line` was er niemand om
 * de aanvraag naar te sturen, en een behandelaar die niemand is, laat een verlofaanvraag
 * verdwijnen. Heeft een inzender géén leidinggevende, dan is er nog steeds niemand - daarom
 * waarschuwt het formulierscherm daarvoor, en blijft de aanvraag zichtbaar voor wie
 * `forms.form.manage` heeft.
 */
export const formHandlingSchema = z.enum(['none', 'group', 'manager']);
export type FormHandling = z.infer<typeof formHandlingSchema>;

export const formVersionStatusSchema = z.enum(['draft', 'published', 'archived']);
export type FormVersionStatus = z.infer<typeof formVersionStatusSchema>;

/**
 * `withdrawn` is de indiener zelf (Extra 6, punt 8).
 *
 * Een vierde status en geen verwijdering: wie vrijdag verlof aanvroeg en het maandag weer
 * intrekt, heeft iets gedaan wat zichtbaar hoort te blijven. Een behandelaar die het dossier
 * later opent, hoort geen gat te vinden waar iemand naar gaat zoeken.
 */
export const submissionStatusSchema = z.enum(['submitted', 'approved', 'rejected', 'withdrawn']);
export type SubmissionStatus = z.infer<typeof submissionStatusSchema>;

// --- Het formulier en zijn versies ---------------------------------------------------

export const formVersionSchema = z.object({
  id: z.guid(),
  formId: z.guid(),
  versionNumber: z.number().int(),
  status: formVersionStatusSchema,
  fields: z.array(formFieldSchema),
  createdAt: z.string().datetime(),
  publishedAt: z.string().datetime().nullable(),
  /** Hoeveel inzendingen tegen déze versie gedaan zijn. */
  submissionCount: z.number().int(),
});
export type FormVersion = z.infer<typeof formVersionSchema>;

export const formSchema = z.object({
  id: z.guid(),
  title: z.string(),
  description: z.string(),
  handling: formHandlingSchema,
  /** Bij `handling: 'group'`: de groep die behandelt. */
  handlerGroupId: z.guid().nullable(),
  handlerGroupName: z.string().nullable(),
  /** Alle behandelgroepen (FBK-7); een lid van eender welke beslist. De eerste is `handlerGroupId`. */
  handlerGroupIds: z.array(z.guid()).default([]),
  handlerGroupNames: z.array(z.string()).default([]),
  /**
   * Behandeld door een groep, maar geen van die groepen bestaat nog: niemand kan de aanvragen
   * opvolgen (hertest 29 okt.). Het beheer toont dan een waarschuwing bij een actief formulier.
   */
  handlerMissing: z.boolean().default(false),
  audienceDescription: z.string(),
  /** De doelgroep zelf (FBK-7), zodat Instellingen ze kan tonen en wijzigen. */
  audience: audienceSpecSchema.nullable().default(null),
  /** Gearchiveerd: niet meer in de app, wel nog hier, in de export en in de inbox (FBK-7). */
  archived: z.boolean().default(false),
  /** De gepubliceerde versie, of `null` als er nog geen is. */
  publishedVersion: formVersionSchema.nullable(),
  /** Het concept waar nog aan gewerkt wordt, of `null`. */
  draftVersion: formVersionSchema.nullable(),
  openSubmissions: z.number().int(),
  /**
   * Alle aanvragen over alle versies heen, ook die van een gearchiveerde versie. Verwijderen kan
   * alleen bij 0; de som van de gepubliceerde versie en het concept gaf het vuilbakje ook bij een
   * formulier met oudere aanvragen (testrapport 28 okt., #10).
   */
  submissionCount: z.number().int().default(0),
  createdAt: z.string().datetime(),
});
export type Form = z.infer<typeof formSchema>;

export const formListSchema = z.object({ items: z.array(formSchema) });
export type FormList = z.infer<typeof formListSchema>;

export const createFormRequestSchema = z.object({
  title: z.string().trim().min(1, 'Geef het formulier een titel.').max(120),
  description: z.string().trim().max(500).default(''),
  audience: audienceSpecSchema,
  handling: formHandlingSchema,
  handlerGroupId: z.guid().nullable().default(null),
  /** Meerdere behandelgroepen (FBK-7). Gaat voor `handlerGroupId` als beide er zijn. */
  handlerGroupIds: z.array(z.guid()).max(10).optional(),
});
export type CreateFormRequest = z.infer<typeof createFormRequestSchema>;

export const updateFormRequestSchema = z.object({
  title: z.string().trim().min(1).max(120).optional(),
  description: z.string().trim().max(500).optional(),
  audience: audienceSpecSchema.optional(),
  handling: formHandlingSchema.optional(),
  handlerGroupId: z.guid().nullable().optional(),
  handlerGroupIds: z.array(z.guid()).max(10).optional(),
  /**
   * De velden van het **concept**.
   *
   * Een gepubliceerde versie wijzigt hier niet van: wie velden aanpast na publicatie, krijgt een
   * nieuw concept dat hij opnieuw publiceert. Anders zou een inzending van vorige maand verwijzen
   * naar een definitie die intussen iets anders zegt.
   */
  fields: z.array(formFieldSchema).max(40).optional(),
});
export type UpdateFormRequest = z.infer<typeof updateFormRequestSchema>;

/** Archiveren of terugzetten (FBK-7). */
export const archiveFormRequestSchema = z.object({ archived: z.boolean() });
export type ArchiveFormRequest = z.infer<typeof archiveFormRequestSchema>;

// --- De medewerker -------------------------------------------------------------------

/** Een formulier zoals de medewerker het ziet: alleen de gepubliceerde versie. */
export const myFormSchema = z.object({
  formId: z.guid(),
  versionId: z.guid(),
  title: z.string(),
  description: z.string(),
  fields: z.array(formFieldSchema),
  /** De waarden die de server al kent; voor `locked`-velden is dit de enige waarheid. */
  prefill: z.record(z.string(), z.string()),
  needsHandling: z.boolean(),
  /** Hoeveel van mijn inzendingen nog in behandeling zijn. */
  openOfMine: z.number().int(),
});
export type MyForm = z.infer<typeof myFormSchema>;

export const myFormListSchema = z.object({ items: z.array(myFormSchema) });
export type MyFormList = z.infer<typeof myFormListSchema>;

/**
 * Een ingevuld formulier.
 *
 * `data` is vrij van vorm omdat de vorm in de definitieversie staat. De server valideert hem
 * tegen die versie en weigert onbekende sleutels — een client die een veld bijverzint, schrijft
 * geen gegevens die niemand ooit nog kan uitleggen.
 */
export const createSubmissionRequestSchema = z.object({
  data: z.record(z.string(), z.unknown()),
});
export type CreateSubmissionRequest = z.infer<typeof createSubmissionRequestSchema>;

export const decideSubmissionRequestSchema = z.object({
  decision: z.enum(['approve', 'reject']),
  /**
   * Waarom. Verplicht bij een afwijzing: "afgekeurd" zonder reden laat een medewerker met een
   * vraag achter die niemand meer beantwoordt.
   */
  note: z.string().trim().max(500).default(''),
});
export type DecideSubmissionRequest = z.infer<typeof decideSubmissionRequestSchema>;

// --- Inzendingen ---------------------------------------------------------------------

export const submissionSchema = z.object({
  id: z.guid(),
  formId: z.guid(),
  formTitle: z.string(),
  versionId: z.guid(),
  versionNumber: z.number().int(),
  /** De velden van de versie waartegen ingediend is, zodat een lezer de waarden kan plaatsen. */
  fields: z.array(formFieldSchema),
  data: z.record(z.string(), z.unknown()),
  status: submissionStatusSchema,
  submittedAt: z.string().datetime(),
  /** De inzender. Bij een formulier mét behandeling is dat geen privacyvraag maar het punt. */
  personId: z.guid(),
  personName: z.string(),
  employeeNumber: z.string().nullable(),
  decidedAt: z.string().datetime().nullable(),
  decidedByName: z.string().nullable(),
  decisionNote: z.string(),
  /** Mag ík over deze inzending beslissen? */
  canDecide: z.boolean(),
  /**
   * Behandelt iemand dit formulier? Bij `handling: 'none'` ("Alleen verzamelen") niet: een
   * ingediende aanvraag is dan **Ontvangen** en niet "In behandeling", telt niet als open en kan
   * niet ingetrokken worden - er komt nooit een beslissing (testrapport 28 okt., #8).
   */
  needsHandling: z.boolean().default(true),
});
export type Submission = z.infer<typeof submissionSchema>;

export const submissionListSchema = z.object({ items: z.array(submissionSchema) });
export type SubmissionList = z.infer<typeof submissionListSchema>;
